import { addDays, endOfMonth, startOfMonth, startOfWeek, subMonths, subWeeks } from "date-fns";
import { getCustomDefaultRange, getLastTwoYearsToYesterdayRange } from "../../utils/utils";
import { DateRangeValue } from "../../utils/types";

export const getDefaultColumnVisibility = (selectedTab: string): Record<string, boolean> => ({
  serialNumber: false,
  description: true,
  account: true,
  assignee: true,
  coach: selectedTab === "aiRecommendedTasks" ? false : true,
  dueDate: true,
  priority: selectedTab === "aiRecommendedTasks" ? false : true,
  status: selectedTab === "aiRecommendedTasks" ? false : true,
  columnFilter: true,
  momlink: false,
  sponsor: false,
  tags: false,
  subscribers: false,
  createdAt: selectedTab === "aiRecommendedTasks" ? true : false,
  actions: selectedTab === "aiRecommendedTasks" ? true : false,
});

export const resolveColumnVisibility = (
  selectedTab: string,
  saved?: Record<string, boolean> | null
): Record<string, boolean> => {
  const visibility = {
    ...getDefaultColumnVisibility(selectedTab),
    ...(saved || {}),
  };

  if (selectedTab !== "aiRecommendedTasks") {
    visibility.actions = false;
  }

  return visibility;
};

export const mapPreferenceColumnsToVisibility = (
  columns: Record<string, boolean> | null | undefined,
  selectedTab: string
): Record<string, boolean> => {
  const defaultVisibility = getDefaultColumnVisibility(selectedTab);

  if (selectedTab === "aiRecommendedTasks" || !columns) {
    return defaultVisibility;
  }

  const columnKeyMap: Record<string, string> = {
    serialNo: "serialnumber",
    title: "description",
    account: "account",
    assignee: "assignee",
    coach: "coach",
    due_date: "dueDate",
    priority: "priority",
    status: "status",
    execSponsor: "sponsor",
    tags: "tags",
    subscribers: "subscribers",
  };

  return Object.entries(columnKeyMap).reduce((acc, [apiKey, localKey]) => {
    if (typeof columns[apiKey] === "boolean") {
      acc[localKey] = columns[apiKey];
    }
    return acc;
  }, { ...defaultVisibility });
};

const buildPresetDateRange = (
  preset: string,
  startDate: Date,
  endDate: Date,
  overdue = false
): DateRangeValue => ({
  preset,
  startDate: new Date(startDate.setHours(0, 0, 0, 0)).toISOString(),
  endDate: new Date(endDate.setHours(23, 59, 59, 999)).toISOString(),
  overdue,
});

export const getDateRangeFromPreference = (
  defaultTimePeriod: string | null | undefined
): DateRangeValue => {
  const normalized = defaultTimePeriod?.toLowerCase().replace(/_/g, "");
  const today = new Date();

  switch (normalized) {
    case "today":
      return buildPresetDateRange("today", new Date(today), new Date(today));
    case "tomorrow": {
      const tomorrow = addDays(today, 1);
      return buildPresetDateRange("tomorrow", new Date(tomorrow), new Date(tomorrow));
    }
    case "currentweek": {
      const start = startOfWeek(today, { weekStartsOn: 0 });
      const end = addDays(start, 6);
      return buildPresetDateRange("currentWeek", start, end);
    }
    case "currentmonth":
      return buildPresetDateRange("currentMonth", startOfMonth(today), endOfMonth(today));
    case "lastweek": {
      const start = startOfWeek(subWeeks(today, 1), { weekStartsOn: 0 });
      const end = addDays(start, 6);
      return buildPresetDateRange("lastWeek", start, end);
    }
    case "lastmonth": {
      const lastMonth = subMonths(today, 1);
      return buildPresetDateRange("lastMonth", startOfMonth(lastMonth), endOfMonth(lastMonth));
    }
    case "overdue": {
      const range = getLastTwoYearsToYesterdayRange();
      return {
        preset: "custom",
        startDate: range.start_time,
        endDate: range.end_time,
        overdue: true,
      };
    }
    default:
      return getCustomDefaultRange();
  }
};